import React, { useRef } from 'react';
import {
  BoolExpr,
  BoolExprGroup,
  ComparisonExpr,
  ComparisonOp,
  LogicalOp,
} from '@/proto/logiblocks/logiblocks';
import { BoolExprEditor } from './BoolExprEditor';
import styles from './ConditionalCustomization.module.css';
import { CustomButton } from '@/components';
import AddButton from '@/components/AddButton';
import { Flex, Select } from 'antd';
import { CustomSelectIcon, DeleteIcon } from '@/assets/icons/icons';

export interface BoolExprGroupEditorProps {
  nodeId: string;
  group: BoolExprGroup;
  onChange: (next: BoolExprGroup) => void;
  isReadOnly?: boolean;
  depth?: number;
  onRemove?: () => void;
}

/** Sentinel value for "operator not yet chosen by the user". */
const OPERATOR_UNSET = -1 as ComparisonOp;

const createEmptyComparisonExpr = (): ComparisonExpr => ({
  variable: undefined,
  operator: OPERATOR_UNSET,
  rhs: undefined,
  rhs2: undefined,
});

// A new nested group is seeded with one default Equals comparison so the
// user immediately sees an editable row instead of an empty bordered box.
const createDefaultGroupChild = (): BoolExpr => ({
  group: {
    expressions: [{ comparison_expr: createEmptyComparisonExpr() }],
    operators: [],
  },
});
const { Option } = Select;
/**
 * Recursive editor for a BoolExprGroup: a list of expressions joined by
 * per-pair AND/OR operators. Maintains the invariant that
 * `operators.length === expressions.length - 1`.
 */
export const BoolExprGroupEditor: React.FC<BoolExprGroupEditorProps> = ({
  nodeId,
  group,
  onChange,
  isReadOnly = false,
  depth = 0,
  onRemove,
}) => {
  // Stable React keys so removing from the middle doesn't cause
  // BoolExprEditor instances to swap state.
  const nextExprKey = useRef(0);
  const exprKeys = useRef<number[]>([]);
  while (exprKeys.current.length < group.expressions.length) {
    exprKeys.current.push(nextExprKey.current++);
  }

  // Replace a single child expression in place; structure is unchanged.
  const updateExpr = (index: number, next: BoolExpr) => {
    const expressions = [...group.expressions];
    expressions[index] = next;
    onChange({ ...group, expressions });
  };

  // Append a new child to the group, defaulting the joining operator to AND
  // (only added when there's already at least one expression). Used by both
  // "Add Expression" and "Add Group".
  const appendChild = (child: BoolExpr) => {
    const expressions = [...group.expressions, child];
    const operators =
      group.expressions.length === 0
        ? group.operators
        : [...group.operators, LogicalOp.AND];
    onChange({ expressions, operators });
  };

  // Append a new comparison leaf.
  const addComparisonExpr = () => {
    appendChild({ comparison_expr: createEmptyComparisonExpr() });
  };

  // Append a new nested group, pre-seeded with one default comparison.
  const addGroup = () => {
    appendChild(createDefaultGroupChild());
  };

  // Delete a child expression and the operator that joined it to its
  // neighbor, preserving `operators.length === expressions.length - 1`.
  const removeExpr = (index: number) => {
    exprKeys.current.splice(index, 1);
    const expressions = group.expressions.filter((_, i) => i !== index);
    const opIndexToDrop = index === 0 ? 0 : index - 1;
    const operators =
      group.operators.length === 0
        ? group.operators
        : group.operators.filter((_, i) => i !== opIndexToDrop);
    onChange({ expressions, operators });
  };

  // Change the AND/OR connective at a specific slot between two expressions.
  const updateOperator = (operatorIndex: number, op: LogicalOp) => {
    const operators = [...group.operators];
    operators[operatorIndex] = op;
    onChange({ ...group, operators });
  };

  return (
    <>
      <Flex vertical gap={16}>
        {group.expressions.map((expr, index) => (
          <React.Fragment key={exprKeys.current[index]}>
            <Flex
              gap={8}
              className={styles.exprCard}
              style={
                !expr.group
                  ? { background: 'transparent' }
                  : depth === 0
                    ? {
                        background: 'var(--light-gray)',
                        borderLeft: '3px solid var(--border)',
                        borderRadius: '8px',
                      }
                    : {
                        background: 'var(--surface-container)',
                        borderLeft: '3px solid var(--border)',
                        borderRadius: '8px',
                      }
              }
            >
              {!expr.group && (
                <BoolExprEditor
                  nodeId={nodeId}
                  expr={expr}
                  onChange={(next) => updateExpr(index, next)}
                  isReadOnly={isReadOnly}
                  whiteInputs={depth === 1}
                />
              )}
              {expr.group && (
                <div className={styles.nestedGroup}>
                  <BoolExprGroupEditor
                    nodeId={nodeId}
                    group={expr.group}
                    onChange={(next) =>
                      updateExpr(index, { ...expr, group: next })
                    }
                    isReadOnly={isReadOnly}
                    depth={depth + 1}
                    onRemove={!isReadOnly ? () => removeExpr(index) : undefined}
                  />
                </div>
              )}
              {!isReadOnly && !expr.group && (
                <button
                  type='button'
                  aria-label='Remove'
                  className={styles.removeButton}
                  onClick={() => removeExpr(index)}
                >
                  <DeleteIcon />
                </button>
              )}
            </Flex>
            {index < group.expressions.length - 1 && (
              <>
                <Select
                  placeholder='Select...'
                  onChange={(e) =>
                    updateOperator(index, Number(e) as LogicalOp)
                  }
                  value={group.operators[index] ?? LogicalOp.AND}
                  disabled={isReadOnly}
                  className='lb-select operator-select'
                  suffixIcon={<CustomSelectIcon color={'#fff'} />}
                  style={{ width: 'fit-content' }}
                  popupMatchSelectWidth={false}
                >
                  <Option value={LogicalOp.AND}>And</Option>
                  <Option value={LogicalOp.OR}>Or</Option>
                </Select>
                {/* <select
                value={group.operators[index] ?? LogicalOp.AND}
                onChange={(e) =>
                  updateOperator(index, parseInt(e.target.value) as LogicalOp)
                }
                className={sharedStyles.selectField}
                disabled={isReadOnly}
              >
                 <option value={LogicalOp.AND}>AND</option>
                <option value={LogicalOp.OR}>OR</option>
               
              </select> */}
              </>
            )}
          </React.Fragment>
        ))}

        {!isReadOnly && (
          <Flex
            align='center'
            justify='space-between'
            className={styles.addButtonsRow}
          >
            <Flex gap={8}>
              <AddButton
                addButtonWidth={82}
                text={'Add condition'}
                onClick={addComparisonExpr}
                padding='6px 12px'
              />
              <AddButton
                addButtonWidth={82}
                text={'Add group'}
                onClick={addGroup}
                padding='6px 12px'
              />
            </Flex>
            {onRemove && (
              <CustomButton
                onClick={onRemove}
                text={'Ungroup'}
                bgColor={'var(--light-gray)'}
                borderRadius={'24px'}
                padding='6px 12px'
                color={'var(--text-primary)'}
                boxShadow='none'
                fontSize='12px'
                fontWeight={600}
                height={'28px'}
              />
            )}
          </Flex>
        )}
      </Flex>
    </>
  );
};
